/**
 * Saved tiles (favorites). Guest key: bd_wishlist.
 * Logged-in: merge Liquid-injected customer.metafields.custom.bd_wishlist.
 * Write path: window.bdWishlistPersist POSTs JSON to /apps/bd-wishlist
 * (Shopify app proxy). Admin token lives only on the app server, never here.
 */
(function () {
  'use strict';

  var KEY = 'bd_wishlist';
  var KLAVIYO_ASKED_KEY = 'bd_wishlist_klaviyo_asked';
  var EVT = 'bd:wishlist:change';
  var TOGGLE = '[data-bd-wishlist-toggle]';
  var GRID_ROOTS = [
    '#filter-results',
    '.main-products-grid__results',
    '.main-products-grid',
    '[data-bd-products-grid]',
    'custom-pagination',
    'product-recommendations',
    'bd-personalized-picks',
    '.product-grid',
    '.collection-grid',
    '[data-product-grid]'
  ];

  function boot() {
    return window.bdWishlistBoot || {};
  }

  function isLoggedIn() {
    return !!boot().customerId;
  }

  function normalizeList(raw) {
    if (!raw) return [];
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw); } catch (err) { return []; }
    }
    if (Array.isArray(raw)) return raw;
    if (raw.items && Array.isArray(raw.items)) return raw.items;
    return [];
  }

  function itemKey(it) {
    if (it && it.id) return 'id:' + String(it.id);
    if (it && it.handle) return 'h:' + it.handle;
    return '';
  }

  function mergeLists(a, b) {
    var out = [];
    var seen = {};
    function add(it) {
      if (!it) return;
      var k = itemKey(it);
      if (!k || seen[k]) return;
      seen[k] = true;
      out.push({
        id: it.id || '',
        handle: it.handle || '',
        title: it.title || '',
        url: it.url || (it.handle ? '/products/' + it.handle : ''),
        image: it.image || ''
      });
    }
    normalizeList(a).forEach(add);
    normalizeList(b).forEach(add);
    return out;
  }

  function readLocal() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return [];
      var data = JSON.parse(raw);
      return Array.isArray(data) ? data : [];
    } catch (err) {
      return [];
    }
  }

  function writeLocal(items) {
    window.localStorage.setItem(KEY, JSON.stringify(items));
  }

  /**
   * Logged-in write: POST items to the in-repo app proxy /apps/bd-wishlist.
   * Guests stay on localStorage only. Override window.bdWishlistPersist if
   * a different writer is installed. No Admin token in this file.
   */
  function defaultPersist(items) {
    var b = boot();
    if (!b.customerId) return Promise.resolve({ skipped: 'guest' });
    // 2026-10-20: the /apps/bd-wishlist app proxy was never installed (404).
    // Stay localStorage-only unless the drawer boot explicitly sets
    // proxyLive: true after the proxy app is deployed. No network call, no warn.
    if (b.proxyLive !== true) return Promise.resolve({ skipped: 'proxy-not-installed' });
    var url = b.proxyPath || '/apps/bd-wishlist';
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      if (!res.ok) {
        throw new Error('wishlist persist ' + res.status);
      }
      return res.json().catch(function () { return { ok: true }; });
    }).catch(function (err) {
      return { skipped: 'proxy', error: err && err.message };
    });
  }

  if (typeof window.bdWishlistPersist !== 'function') {
    window.bdWishlistPersist = defaultPersist;
  }

  function persistRemote(items) {
    if (!isLoggedIn()) return;
    if (typeof window.bdWishlistPersist === 'function') {
      try { window.bdWishlistPersist(items); } catch (err) { /* ignore */ }
    }
  }

  function write(items) {
    writeLocal(items);
    persistRemote(items);
    document.dispatchEvent(new CustomEvent(EVT, { detail: { items: items } }));
  }

  function read() {
    return readLocal();
  }

  function mergeGuestWithMetafield() {
    if (!isLoggedIn()) return readLocal();
    var merged = mergeLists(readLocal(), boot().metafield);
    writeLocal(merged);
    persistRemote(merged);
    return merged;
  }

  function isSaved(items, id, handle) {
    var sid = id != null ? String(id) : '';
    return items.some(function (it) {
      return (sid && String(it.id) === sid) || (handle && it.handle === handle);
    });
  }

  function productFromEl(el) {
    return {
      id: el.getAttribute('data-product-id') || '',
      handle: el.getAttribute('data-product-handle') || '',
      title: el.getAttribute('data-product-title') || '',
      url: el.getAttribute('data-product-url') || '',
      image: el.getAttribute('data-product-image') || ''
    };
  }

  function toggle(product) {
    var items = read();
    var idx = items.findIndex(function (it) {
      return (product.id && String(it.id) === String(product.id)) ||
        (product.handle && it.handle === product.handle);
    });
    if (idx >= 0) {
      items.splice(idx, 1);
    } else {
      items.unshift({
        id: product.id,
        handle: product.handle,
        title: product.title,
        url: product.url || (product.handle ? '/products/' + product.handle : ''),
        image: product.image || ''
      });
    }
    write(items);
    maybeAskKlaviyo();
  }

  function removeItem(id, handle) {
    write(read().filter(function (it) {
      return !(
        (id && String(it.id) === String(id)) ||
        (handle && it.handle === handle)
      );
    }));
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, function (ch) {
      return ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      })[ch];
    });
  }

  function renderDrawer(items) {
    var empty = document.getElementById('bd-wishlist-empty');
    var list = document.getElementById('bd-wishlist-list');
    if (!empty || !list) return;

    if (!items.length) {
      empty.hidden = false;
      list.hidden = true;
      list.innerHTML = '';
      return;
    }

    empty.hidden = true;
    list.hidden = false;
    list.innerHTML = items.map(function (it) {
      var href = escapeHtml(it.url || (it.handle ? '/products/' + it.handle : '#'));
      var title = escapeHtml(it.title || it.handle || 'Saved tile');
      var img = it.image
        ? '<img src="' + escapeHtml(it.image) + '" alt="" width="72" height="72" loading="lazy">'
        : '';
      return (
        '<li class="bd-wishlist-item">' +
          '<a class="bd-wishlist-item__link" href="' + href + '">' +
            '<span class="bd-wishlist-item__media">' + img + '</span>' +
            '<span class="bd-wishlist-item__title">' + title + '</span>' +
          '</a>' +
          '<button type="button" class="bd-wishlist-item__remove" data-bd-wishlist-remove' +
            ' data-product-id="' + escapeHtml(it.id) + '"' +
            ' data-product-handle="' + escapeHtml(it.handle) + '"' +
            ' aria-label="Remove ' + title + ' from saved tiles">&times;</button>' +
        '</li>'
      );
    }).join('');
  }

  function collectToggles(root) {
    var scope = root && root.nodeType === 1 ? root : document;
    var nodes = [];
    if (scope.matches && scope.matches(TOGGLE)) nodes.push(scope);
    if (scope.querySelectorAll) {
      var found = scope.querySelectorAll(TOGGLE);
      for (var i = 0; i < found.length; i++) nodes.push(found[i]);
    }
    return nodes;
  }

  function syncHearts(root, items) {
    items = items || read();
    collectToggles(root).forEach(function (btn) {
      var saved = isSaved(
        items,
        btn.getAttribute('data-product-id'),
        btn.getAttribute('data-product-handle')
      );
      btn.classList.toggle('is-saved', saved);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      var title = btn.getAttribute('data-product-title') || 'tile';
      btn.setAttribute('aria-label', saved ? ('Saved ' + title) : ('Save ' + title));
    });
    return items;
  }

  function sync(items, root) {
    items = items || read();
    syncHearts(root || document, items);

    var count = items.length;
    document.querySelectorAll('.bd-wishlist-header-count').forEach(function (badge) {
      badge.hidden = count === 0;
      var num = badge.querySelector('[data-bd-wishlist-count], .bd-wishlist-header-count-number');
      if (num) num.textContent = String(count);
    });

    renderDrawer(items);
  }

  function isOpen() {
    var drawer = document.getElementById('bd-wishlist-drawer');
    return !!(drawer && drawer.hasAttribute('open'));
  }

  var lastFocus = null;
  var trapHandler = null;

  function focusableIn(root) {
    if (!root) return [];
    return Array.prototype.slice.call(root.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) {
      return !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true';
    });
  }

  function bindFocusTrap(drawer) {
    unbindFocusTrap();
    trapHandler = function (event) {
      if (event.key !== 'Tab' || !isOpen()) return;
      var nodes = focusableIn(drawer);
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trapHandler, true);
  }

  function unbindFocusTrap() {
    if (trapHandler) {
      document.removeEventListener('keydown', trapHandler, true);
      trapHandler = null;
    }
  }

  function openDrawer() {
    var drawer = document.getElementById('bd-wishlist-drawer');
    var overlay = document.getElementById('bd-wishlist-overlay');
    if (!drawer) return;
    lastFocus = document.activeElement;
    renderDrawer(read());
    drawer.setAttribute('open', '');
    if (overlay) overlay.hidden = false;
    document.documentElement.classList.add('bd-wishlist-open');
    document.querySelectorAll('[data-bd-wishlist-open]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'true');
    });
    var closeBtn = drawer.querySelector('[data-bd-wishlist-close]');
    if (closeBtn) closeBtn.focus();
    bindFocusTrap(drawer);
    maybeAskKlaviyo();
  }

  function closeDrawer() {
    var drawer = document.getElementById('bd-wishlist-drawer');
    var overlay = document.getElementById('bd-wishlist-overlay');
    if (drawer) drawer.removeAttribute('open');
    if (overlay) overlay.hidden = true;
    document.documentElement.classList.remove('bd-wishlist-open');
    document.querySelectorAll('[data-bd-wishlist-open]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
    unbindFocusTrap();
    if (lastFocus && typeof lastFocus.focus === 'function') {
      try { lastFocus.focus(); } catch (err) { /* ignore */ }
    }
    lastFocus = null;
  }

  function klaviyoAskedMap() {
    try {
      var raw = window.localStorage.getItem(KLAVIYO_ASKED_KEY);
      var data = raw ? JSON.parse(raw) : {};
      return data && typeof data === 'object' ? data : {};
    } catch (err) {
      return {};
    }
  }

  function hasAskedKlaviyo() {
    var id = String(boot().customerId || '');
    if (!id) return true;
    return !!klaviyoAskedMap()[id];
  }

  function markAskedKlaviyo() {
    var id = String(boot().customerId || '');
    if (!id) return;
    var map = klaviyoAskedMap();
    map[id] = true;
    window.localStorage.setItem(KLAVIYO_ASKED_KEY, JSON.stringify(map));
  }

  function showKlaviyoAsk() {
    var panel = document.getElementById('bd-wishlist-klaviyo');
    if (!panel) return;
    panel.hidden = false;
    var box = document.getElementById('bd-wishlist-klaviyo-consent');
    if (box) box.checked = false;
  }

  function hideKlaviyoAsk() {
    var panel = document.getElementById('bd-wishlist-klaviyo');
    if (panel) panel.hidden = true;
  }

  /**
   * Existing onsite path (preview HTML):
   *   static.klaviyo.com/onsite/js/VAwCQY/klaviyo.js?company_id=VAwCQY
   *   window.klaviyo / window._klOnsite
   * No list or form id is in the theme. identify() is the public hook.
   * Jay can set window.bdWishlistBoot.klaviyoListId or window.bdWishlistKlaviyoSubscribe.
   */
  function subscribeKlaviyo(email) {
    if (typeof window.bdWishlistKlaviyoSubscribe === 'function') {
      return window.bdWishlistKlaviyoSubscribe(email, boot());
    }
    var payload = {
      email: email,
      $email: email,
      $consent: ['email'],
      $source: 'bd_wishlist_favorites'
    };
    var kv = window.klaviyo;
    if (kv && typeof kv.identify === 'function') {
      return kv.identify(payload);
    }
    if (kv && typeof kv.push === 'function') {
      kv.push(['identify', payload]);
      return;
    }
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['identify', payload]);
  }

  function maybeAskKlaviyo() {
    if (!isLoggedIn() || hasAskedKlaviyo()) return;
    if (!isOpen()) openDrawer();
    showKlaviyoAsk();
  }

  function finishKlaviyoAsk(accepted) {
    var status = document.getElementById('bd-wishlist-klaviyo-status');
    var box = document.getElementById('bd-wishlist-klaviyo-consent');
    if (accepted) {
      if (!box || !box.checked) {
        if (status) {
          status.hidden = false;
          status.textContent = 'Check the box if you want emails.';
        }
        return;
      }
      var email = boot().customerEmail;
      if (email) subscribeKlaviyo(email);
      if (status) {
        status.hidden = false;
        status.textContent = email
          ? 'Thanks — we will use the existing Klaviyo onsite identify hook.'
          : 'Signed in, but no email was available on the customer object.';
      }
    }
    markAskedKlaviyo();
    if (!accepted) hideKlaviyoAsk();
    else hideKlaviyoAsk();
  }

  document.addEventListener('click', function (event) {
    var toggleBtn = event.target.closest('[data-bd-wishlist-toggle]');
    if (toggleBtn) {
      event.preventDefault();
      event.stopPropagation();
      toggle(productFromEl(toggleBtn));
      return;
    }

    var openBtn = event.target.closest('[data-bd-wishlist-open]');
    if (openBtn) {
      event.preventDefault();
      event.stopPropagation();
      if (isOpen()) closeDrawer();
      else openDrawer();
      return;
    }

    var removeBtn = event.target.closest('[data-bd-wishlist-remove]');
    if (removeBtn) {
      event.preventDefault();
      event.stopPropagation();
      removeItem(
        removeBtn.getAttribute('data-product-id'),
        removeBtn.getAttribute('data-product-handle')
      );
      return;
    }

    if (event.target.closest('[data-bd-wishlist-klaviyo-yes]')) {
      event.preventDefault();
      finishKlaviyoAsk(true);
      return;
    }

    if (event.target.closest('[data-bd-wishlist-klaviyo-no]')) {
      event.preventDefault();
      finishKlaviyoAsk(false);
      return;
    }

    if (event.target.closest('[data-bd-wishlist-close]')) {
      event.preventDefault();
      closeDrawer();
    }
  }, true);

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && isOpen()) {
      closeDrawer();
    }
  });

  document.addEventListener(EVT, function (event) {
    sync(event.detail && event.detail.items);
  });

  function bootSync() {
    sync(mergeGuestWithMetafield());
    observeGrids();
    if (isLoggedIn() && !hasAskedKlaviyo()) {
      var ref = document.referrer || '';
      if (/customer_authentication|\/account/i.test(ref)) {
        maybeAskKlaviyo();
      }
    }
  }

  document.addEventListener('DOMContentLoaded', bootSync);

  document.addEventListener('shopify:section:load', function (event) {
    sync(read(), event && event.target);
  });

  document.addEventListener('on:facet-filters:updated', function () {
    sync(read());
  });
  document.addEventListener('on:pagination:loaded', function () { sync(read()); });
  document.addEventListener('on:pagination:updated', function () { sync(read()); });

  document.addEventListener('on:bfcache:load-restore', function () {
    sync(read());
  });
  document.addEventListener('on:bd-affinity:ready', function () {
    window.setTimeout(function () { syncHearts(document, read()); }, 0);
  });

  var syncTimer = null;
  function scheduleHeartSync(root) {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(function () {
      syncHearts(root || document, read());
    }, 30);
  }

  function nodeHasToggles(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.matches && node.matches(TOGGLE)) return true;
    return !!(node.querySelector && node.querySelector(TOGGLE));
  }

  function observeGrids() {
    if (window.bdWishlistGridObserver) return;
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        if (!added || !added.length) continue;
        for (var j = 0; j < added.length; j++) {
          if (nodeHasToggles(added[j])) {
            scheduleHeartSync(added[j].nodeType === 1 ? added[j] : document);
            return;
          }
        }
      }
    });
    window.bdWishlistGridObserver = observer;

    var observed = false;
    GRID_ROOTS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        observer.observe(el, { childList: true, subtree: true });
        observed = true;
      });
    });
    // Do not watch document.documentElement — GRID_ROOTS / product-grid is enough.
    return observed;
  }

  if (document.readyState !== 'loading') {
    bootSync();
  }
})();
